import { motion } from 'framer-motion'
import { ArrowRight, Calendar, MapPin, Truck, User } from 'lucide-react'
import { useMemo } from 'react'

import type { Client, Comanda, Masina, Sofer } from '@/db/dexie'
import {
  STATUS_ORDER,
  fmtDataRelativa,
  fmtKg,
  fmtRON,
  statusMeta,
  statusUrmator,
} from '@/lib/format'
import { cn } from '@/lib/utils'

/**
 * Comenzi funnel board — the six statuses as columns, cards grouped by status.
 * Cards carry a shared `layoutId` so framer-motion animates them across
 * columns when a comandă advances (see `onAdvance`). Horizontally scrollable
 * so the whole pipeline stays legible on a phone.
 */

const EASE = [0.16, 1, 0.3, 1] as const

export interface KanbanBoardProps {
  comenzi: Comanda[]
  clientiById: Map<string, Client>
  soferiById: Map<string, Sofer>
  masiniById: Map<string, Masina>
  onOpen: (id: string) => void
  onAdvance: (id: string) => void
}

export function KanbanBoard({
  comenzi,
  clientiById,
  soferiById,
  masiniById,
  onOpen,
  onAdvance,
}: KanbanBoardProps) {
  const columns = useMemo(
    () =>
      STATUS_ORDER.map((status) => {
        const items = comenzi
          .filter((c) => c.status === status)
          .sort((a, b) => (a.dataLivrare < b.dataLivrare ? -1 : 1))
        return { status, items, totalRON: items.reduce((s, c) => s + c.totalRON, 0) }
      }),
    [comenzi],
  )

  return (
    <div
      data-tour="comenzi-board"
      className="-mx-1 overflow-x-auto px-1 pb-4 [scrollbar-width:thin]"
    >
      <div className="flex min-w-max items-stretch gap-3">
        {columns.map((col) => {
          const meta = statusMeta[col.status]
          return (
            <section key={col.status} className="flex w-[272px] shrink-0 flex-col">
              <header className="mb-3 flex items-center justify-between gap-2 rounded-lg border border-border bg-card px-3 py-2 shadow-paper">
                <div className="flex min-w-0 items-center gap-2">
                  <span
                    className="size-2 shrink-0 rounded-full"
                    style={{ backgroundColor: meta.dot }}
                    aria-hidden
                  />
                  <span className="truncate text-sm font-medium" style={{ color: meta.fg }}>
                    {meta.label}
                  </span>
                  <span className="nums rounded-full bg-muted px-1.5 text-xs font-medium text-muted-foreground">
                    {col.items.length}
                  </span>
                </div>
                <span className="nums shrink-0 font-mono text-[11px] text-muted-foreground">
                  {col.totalRON > 0 ? fmtRON(col.totalRON) : '—'}
                </span>
              </header>

              <div className="flex flex-1 flex-col gap-2.5">
                {col.items.map((c) => (
                  <KanbanCard
                    key={c.id}
                    comanda={c}
                    client={clientiById.get(c.clientId)}
                    sofer={c.soferId ? soferiById.get(c.soferId) : undefined}
                    masina={c.masinaId ? masiniById.get(c.masinaId) : undefined}
                    onOpen={onOpen}
                    onAdvance={onAdvance}
                  />
                ))}
                {col.items.length === 0 && (
                  <div className="rounded-lg border border-dashed border-border/70 px-3 py-8 text-center text-xs text-muted-foreground/60">
                    fără comenzi
                  </div>
                )}
              </div>
            </section>
          )
        })}
      </div>
    </div>
  )
}

function KanbanCard({
  comanda,
  client,
  sofer,
  masina,
  onOpen,
  onAdvance,
}: {
  comanda: Comanda
  client?: Client
  sofer?: Sofer
  masina?: Masina
  onOpen: (id: string) => void
  onAdvance: (id: string) => void
}) {
  const next = statusUrmator(comanda.status)
  return (
    <motion.article
      layout
      layoutId={comanda.id}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ layout: { duration: 0.5, ease: EASE }, duration: 0.3, ease: EASE }}
      onClick={() => onOpen(comanda.id)}
      className="group cursor-pointer rounded-lg border border-border bg-card p-3 shadow-paper transition-shadow hover:shadow-paper-md"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium leading-tight">
            {client?.nume ?? comanda.clientId}
          </p>
          <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
            <MapPin className="size-3 shrink-0" />
            <span className="truncate">{client?.localitate ?? '—'}</span>
          </p>
        </div>
        <span className="nums shrink-0 font-mono text-[10px] text-muted-foreground/70">
          {comanda.id.replace('cmd-', '#')}
        </span>
      </div>

      <div className="mt-3 flex items-baseline justify-between gap-2">
        <span className="nums font-mono text-sm font-semibold text-amber-ink">
          {fmtRON(comanda.totalRON)}
        </span>
        <span className="nums font-mono text-xs text-muted-foreground">
          {fmtKg(comanda.totalKg)}
        </span>
      </div>

      <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
        <span className="inline-flex items-center gap-1 rounded-md bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
          <Calendar className="size-3" />
          {fmtDataRelativa(comanda.dataLivrare)}
        </span>
        {sofer && (
          <span className="inline-flex items-center gap-1 rounded-md bg-pine-soft px-1.5 py-0.5 text-[11px] text-pine">
            <User className="size-3" />
            {sofer.nume.split(' ')[0]}
          </span>
        )}
        {masina && (
          <span className="nums inline-flex items-center gap-1 rounded-md bg-secondary px-1.5 py-0.5 font-mono text-[11px] text-secondary-foreground">
            <Truck className="size-3" />
            {masina.nrInmatriculare}
          </span>
        )}
      </div>

      {next && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onAdvance(comanda.id)
          }}
          className={cn(
            'mt-3 flex w-full items-center justify-center gap-1 rounded-md border border-border/70 bg-background/50 py-1.5',
            'text-xs font-medium text-amber-ink opacity-80 transition-all',
            'hover:border-amber hover:bg-amber-soft hover:opacity-100 active:scale-[0.98]',
          )}
        >
          Avansează
          <ArrowRight className="size-3" />
        </button>
      )}
    </motion.article>
  )
}
